<template>
  <div class="container-max-width">
    <p class="accent title">
      {{ titleSetting }}
      <span class="dim">
        {{ textValueCurrent }}
        {{ userStore.userPreferences[props.preferenceTarget] * 16 }}px
      </span>
    </p>
    <div class="wrapper-control">
      <div class="wrapper-width">
        <button
          class="button-primary button-small button-rounded button-icon"
          @click="inputDecrease"
          :disabled="isDisabledButtonDecrease"
        >
          <IconMinus class="icon-smaller" />
        </button>
        <div class="input-with-unit">
          <InputNumberWithComma v-model="inputPx" :valueMin="inputMinimum" />
        </div>

        <button class="button-primary button-small button-rounded button-icon" @click="inputIncrease">
          <IconPlus class="icon-smaller" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { debounce } from 'lodash';

import IconMinus from '@/components/icons/IconMinus.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import InputNumberWithComma from '@/components/shared/base/InputNumberWithComma.vue';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';

const props = withDefaults(
  defineProps<{
    isGsc?: boolean;
    isGlobal?: boolean;
    defaultValue?: number;
    preferenceTarget: string;
    titleSetting: string;
    textValueCurrent: string;
  }>(),
  { isGsc: false, isGlobal: false }
);

const { t } = useI18n();
const toastStore = useToastStore();

// 사용자설정가져오기
const userStore = useUserStore();

const inputPxDefault = props.defaultValue ?? userStore.userPreferences[props.preferenceTarget] * 16;
const inputPx = ref(inputPxDefault); // 미확정 너비
const inputIncrement = 10; // 너비 조절 단위

const inputMinimum = computed(() => {
  return (
    {
      screenContainerHeight: 500,
      screenMessageWidth: 720,
      subtitleWidth: 300,
    }[props.preferenceTarget] || 720
  );
});

const isDisabledButtonDecrease = computed(() => {
  return inputPx.value <= inputMinimum.value;
});

// 높이 조절을 위한 설정인지 반환
const isTargetHeight = computed(() => {
  return props.preferenceTarget === 'screenContainerHeight';
});

// 너비 증가
const inputIncrease = () => {
  inputPx.value += inputIncrement;
  if (props.isGsc) {
    inputConfirm();
  }
};

// 너비 감소
const inputDecrease = () => {
  const inputDecremented = inputPx.value - inputIncrement;

  if (inputDecremented < inputMinimum.value) {
    inputPx.value = inputMinimum.value;
    return;
  } else {
    inputPx.value = inputDecremented;
  }

  if (props.isGsc) {
    inputConfirm();
  }
};

// 너비 값 검토
const inputValidate = () => {
  if (inputPx.value < inputMinimum.value) {
    const textFail = isTargetHeight.value ? 'screen_height_adjust_fail' : 'screen_width_adjust_fail';
    inputPx.value = userStore.userPreferences[props.preferenceTarget] * 16;
    toastStore.error(t(textFail, { inputMinimum: inputMinimum.value }));
    return false;
  }
  return true;
};

// 너비 확정
const inputConfirm = () => {
  const resultValidation = inputValidate();
  if (!resultValidation) return;

  userStore.updateUserPreferences({
    setting: props.preferenceTarget,
    newVal: inputPx.value / 16,
  });

  if (props.isGsc) return true;

  const textSuccess = isTargetHeight.value ? 'screen_height_adjust' : 'screen_width_adjust';
  toastStore.info(t(textSuccess));
};

const debounceConfirm = debounce(() => {
  inputConfirm();
}, 800);

watch(inputPx, (newVal, oldVal) => {
  if (newVal === oldVal) return;

  debounceConfirm();
});

watch(
  () => userStore.userPreferences[props.preferenceTarget],
  (newVal) => {
    inputPx.value = newVal * 16;
  }
);

watch(
  () => props.defaultValue,
  (newVal) => {
    if (newVal) {
      inputPx.value = newVal;
    }
  },
  { immediate: true }
);

defineExpose({
  inputPx,
  inputConfirm,
});
</script>

<style scoped>
.container-max-width {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  gap: 0.5rem;
}

.title {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.container-control {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.wrapper-control {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
}

.wrapper-width {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

input[type='number'] {
  width: 100%;
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

.input-with-unit {
  position: relative;
  display: inline-block;
}

.input-with-unit input {
  padding-right: 2.5rem;
}

.input-with-unit::after {
  content: 'px';
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--font-dim);
  pointer-events: none;
}
</style>
